import { Icon, useConfirm, useAlert } from '@genial/components';
import { Service } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { screenName } from '../../../../../../../store/customerDetail/constants';
import { movementOptionQueryState } from '../../../../../../../store/customerDetail/movement/atom';
import {
  FinancialMovementsResponseNormalized,
  MovementOptionQuery,
} from '../../../../../../../store/customerDetail/movement/types';
import { advisorIdToApisSelector } from '../../../../../../../store/master/selector';
import { messageError, validateAndFormatValue } from '../../Statements/Table/constants';

import { DesktopItem } from './desktop';
import { MobileItem } from './mobile';
import { TabletItem } from './tablet';

interface ItemProps extends FinancialMovementsResponseNormalized {}

export interface ItemResponsiveProps {
  productType: MovementOptionQuery;
  descriptionType: string;
  description: string;
  requestedDate: string;
  settlementDate: string;
  formattedValue: string;
  quantity: string;
  asset: string;
  statusIcon: React.ReactNode;
  statusDescription: string;
  cancelButtonIsDisabled: boolean;
  valueIsPositive: boolean;
  handleCancelMovement: () => void;
}

export const Item: React.FC<ItemProps> = (props) => {
  const { confirm } = useConfirm();
  const { alert } = useAlert();

  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });

  const productType = useRecoilValue(movementOptionQueryState);
  const advisorSelected = useRecoilValue(advisorIdToApisSelector);

  const descriptionType = props.product || messageError;
  const requestedDate = validateAndFormatValue(props.requestDate as Date, 'date');
  const settlementDate = validateAndFormatValue(props.settlementDate as Date, 'date');
  const description = props?.type || messageError;
  const value = props.value || 0;
  const valueIsPositive = value > 0;
  const quantity = props.requestedAmount.toFixed(2);
  const formattedValue = validateAndFormatValue(value, 'money');
  const asset = props?.asset || messageError;

  const [statusDescription, setStatusDescription] = useState(
    props?.status ? props?.status?.toUpperCase() : messageError
  );
  const [cancelButtonIsDisabled, setCancelButtonIsDisabled] = useState(!props?.canCancel);

  const statusIcon = (
    <Icon
      name={props.statusIcon.iconNormalized}
      size={props.statusIcon.iconSizeNormalized}
      color={props.statusIcon.iconColorNormalized}
      backgroundColor={props.statusIcon.backgroundColorNormalized}
      variant="rounded"
    />
  );

  const handleCancelMovement = () => {
    confirm(
      {
        isMobile,
        icon: 'Error',
        iconColor: 'feedback.error.default',
        title: 'Cancelamento',
        description: 'Deseja cancelar a movimentação do ativo aberto?',
        confirmText: 'Confirmar',
        cancelText: 'Fechar',
      },
      async () => {
        try {
          await Service.Finance.V1.postMovementCancel({
            screenName,
            financialMovementsCancelRequest: {
              movementId: props.movementId,
              accountId: props.accountId,
              isOrder: props.isOrder,
              productType,
              status: props.status!,
              advisorId: advisorSelected!,
            },
          });
          setCancelButtonIsDisabled(true);
          setStatusDescription('CANCELADO');
        } catch (_error) {
          alert({
            description:
              'Não foi possível cancelar a movimentação selecionada.\nTente novamente mais tarde.',
            icon: 'Warning',
            iconColor: 'feedback.attention.default',
          });
        }
      }
    );
  };

  const responsiveProps = {
    productType,
    descriptionType,
    description,
    requestedDate,
    settlementDate,
    formattedValue,
    quantity,
    asset,
    statusIcon,
    statusDescription,
    cancelButtonIsDisabled,
    valueIsPositive,
    handleCancelMovement,
  };

  if (isMobile) {
    return <MobileItem {...responsiveProps} />;
  }

  if (isTablet) {
    return <TabletItem {...responsiveProps} />;
  }

  return <DesktopItem {...responsiveProps} />;
};
